import { supabase } from './supabase';
import { setCacheOwner } from '../utils/sessionCache';
import { limpiarRespuestasDelCuestionario } from '../components/onboarding/accountSetup';

/**
 * Cierre de sesión único de la app — ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas.
 *
 * Antes había CUATRO logouts hechos a mano (`Sidebar`, `config_sections/Account`,
 * `config_sections/Company`, `estilos/EstiloMinimal`), todos iguales:
 * `await signOut(); navigate('/auth')`. Ninguno reseteaba el estado del cliente, y
 * ese era el bug: `navigate` es una navegación de React Router, no recarga la
 * página, así que TODO el estado a nivel de módulo sobrevivía al cambio de cuenta
 * —el `Map` de `utils/sessionCache` entre otros—. Quien salía y entraba con otra
 * cuenta se encontraba con los datos de la primera.
 *
 * Que fueran cuatro copias es lo que hizo que el bug fuera de esta clase y no un
 * descuido puntual: cualquier limpieza había que acordarse de repetirla cuatro
 * veces. Por eso ahora hay una sola función y las cuatro pantallas la llaman.
 *
 * El orden importa:
 *  1. `signOut()` — con respaldo `scope: 'local'`, el mismo criterio que ya usaba
 *     `AccountSetupLayout`: si el pedido no llegó al servidor, la sesión local
 *     igual tiene que morir, si no la guardia de `/auth` rebota al home.
 *  2. Limpieza de lo que sobrevive a una recarga (localStorage).
 *  3. Navegación DURA. Es la que hace el trabajo pesado: al recargar el documento
 *     se tira todo el estado en memoria de una —`sessionCache`, las URLs firmadas
 *     de `utils/imageCache`, `lib/primeraGeneracion`, `pages/v2/promptHistory`,
 *     las props de PostHog, promesas en vuelo, el árbol de React entero—, sin
 *     depender de que alguien haya enumerado cada cache. Un logout es raro y va a
 *     una pantalla liviana: el costo de la recarga se paga una vez y compra que
 *     un cache futuro que nadie se acuerde de limpiar ya nazca protegido.
 *
 * Lo que la recarga NO limpia es `sessionStorage`, que muere recién al cerrar la
 * pestaña: esas llaves las tira `setCacheOwner` al quedarse sin dueño
 * (ADR-2026-09-11-el-cache-de-sessionstorage-tambien-tiene-dueno). Una llave nueva
 * de `sessionStorage` que describa a la cuenta va en la lista de ese módulo, igual
 * que las de `localStorage` van en `CLAVES_DE_CUENTA` acá abajo.
 */
export async function cerrarSesion(destino = '/auth'): Promise<void> {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // Sin red no hay nada más que intentar contra el servidor; lo que no puede
    // pasar es quedarnos con la sesión local viva.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      /* noop */
    }
  }

  limpiarEstadoDeCuenta();

  // `replace` y no `assign`: además de recargar, saca del historial la pantalla
  // con sesión. Con `assign` quedaría una entrada atrás y el "volver" del
  // navegador podría restaurarla desde el bfcache —pintada con los datos de la
  // cuenta que se acaba de ir— antes de que la guardia alcance a rebotar.
  window.location.replace(destino);
}

/**
 * Llaves de localStorage con datos de UNA cuenta y nombre global (sin el id del
 * usuario adentro). Sobreviven al logout y también a cerrar el navegador, así que
 * la recarga de `cerrarSesion` no alcanza: hay que borrarlas a mano.
 *
 * El criterio para entrar en esta lista es "describe a una cuenta", no "es un
 * dato de UI". Quedan AFUERA a propósito las preferencias del dispositivo
 * (`atlas-v2-last-format`, la sidebar colapsada, el carrusel de loading) y la
 * atribución de marketing (`atlas:ref`, `atlas:fbc`, `atlas:fbp`, y
 * `atlas:ultimo-login`, que existe justamente para sobrevivir al logout y
 * ofrecerle al usuario el método con el que entró la vez pasada).
 *
 * Si agregás una llave nueva que describa a la cuenta, va acá.
 */
const CLAVES_DE_CUENTA = [
  // Semilla de gating del plan: la leen `InterfazV2`, `Modos`, `useModosGenerar`,
  // `AsistenteMode`, `SheetModosProducto` y el `Sidebar` en el PRIMER render,
  // antes de que llegue su fetch. Sin limpiarla, quien entra después ve por unos
  // instantes las capacidades del plan del anterior.
  'atlas-user-plan',
  // `dev_tester` + `grupo_testing`. Es la más delicada de la lista: `dev_tester`
  // es lo que gatea los accesos internos de la sidebar.
  'atlas-user-group',
  // Rubro de la marca activa: decide qué vertical de la UI se muestra.
  'atlas-rubro-marca-activa',
  // Anuncio a medio armar, con los ids de variante de los productos de la
  // empresa anterior adentro y 6 horas de TTL.
  'atlas:crear-anuncio:last-state',
  // Generaciones pendientes: el flujo de recuperación las levanta al entrar.
  'atlas-onboarding-pending-generation',
  'atlas-v2-pending-generation',
  // Cuenta de Instagram vinculada.
  'instagram_auth_user_id',
  // El onboarding de marca a medio hacer: empresa, marca, respuestas y el lote en vuelo.
  // Otra cuenta que abriera un paso guardado arrancaba con los ids de ésta
  // (review Greptile #164, ADR-2026-10-07-el-onboarding-a-medio-hacer-es-de-una-cuenta).
  'atlas:onboarding_marca',
];

/**
 * Deja el navegador sin rastros de la cuenta que se va. Separada de
 * `cerrarSesion` para que la puedan usar los flujos que cierran sesión con otro
 * destino, y para poder testearla sin tocar `window.location`.
 */
export function limpiarEstadoDeCuenta(): void {
  // El cache en memoria queda sin dueño: aunque no hubiera recarga, ya no le
  // sirve a nadie lo que guardó esta cuenta.
  setCacheOwner(null);

  for (const clave of CLAVES_DE_CUENTA) {
    try {
      localStorage.removeItem(clave);
    } catch {
      /* storage bloqueado (Safari privado): no hay nada que limpiar */
    }
  }

  // Las respuestas del alta ya tenían su propia limpieza por este mismo motivo
  // (ADR-2026-08-03-volver-en-el-paso-1-desloguea); se reusa en vez de repetir las llaves.
  limpiarRespuestasDelCuestionario();
}
